import { useLayoutEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router';

import { useGlass } from '@components/glass/useGlass';
import { NAV_ITEMS } from '@config/site';
import { cx } from '@utils/cx';

import styles from './NavLinks.module.scss';

/**
 * Desktop navigation, with a glass lens that slides to the current page.
 *
 * The lens tells the visitor where they are, and its slide tells them
 * where they came from. Position is measured from the active link and
 * written as custom properties, so moving it never re-renders anything.
 * The ResizeObserver catches the reflow when web fonts arrive.
 */
export const NavLinks = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const lensRef = useRef<HTMLSpanElement>(null);
  const lens = useGlass(lensRef, { variant: 'clear', className: styles.lens });

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const place = (): void => {
      const active = wrapper.querySelector<HTMLElement>('[aria-current="page"]');
      wrapper.toggleAttribute('data-has-active', active !== null);
      if (!active) return;
      wrapper.style.setProperty('--lens-x', `${String(active.offsetLeft)}px`);
      wrapper.style.setProperty('--lens-width', `${String(active.offsetWidth)}px`);
    };

    place();
    const observer = new ResizeObserver(place);
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
    };
  }, [pathname]);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <span ref={lensRef} className={lens.className} style={lens.style} aria-hidden="true">
        {lens.filter}
      </span>
      <ul className={styles.list} role="list">
        {NAV_ITEMS.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              viewTransition
              className={({ isActive }) => cx(styles.link, isActive && styles.active)}
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
};
